import { z } from "zod";
import { postingKeyRefSchema } from "../accounting/posting-keys.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
// ── Sabitler ─────────────────────────────────────────────────────────────────
export const COUNTERPARTY_ROLES = [
    "CUSTOMER",
    "SUPPLIER",
    "DISTRIBUTOR",
    "PERSONNEL",
    "FLEET_CUSTOMER",
    "OTHER",
];
export const counterpartyRoleSchema = z.enum(COUNTERPARTY_ROLES);
export const COUNTERPARTY_ROLE_LABELS = {
    CUSTOMER: "Müşteri",
    SUPPLIER: "Tedarikçi",
    DISTRIBUTOR: "Dağıtıcı",
    PERSONNEL: "Personel",
    FLEET_CUSTOMER: "Filo müşterisi",
    OTHER: "Diğer",
};
export const counterpartyKindSchema = z.enum(["LEGAL", "INDIVIDUAL"]);
export const COUNTERPARTY_KIND_LABELS = {
    LEGAL: "Tüzel kişi",
    INDIVIDUAL: "Gerçek kişi",
};
export const counterpartyPaymentMethodSchema = z.enum([
    "CASH",
    "BANK_TRANSFER",
    "CREDIT_CARD",
    "CHEQUE",
    "NOTE",
    "DBS",
    "OTHER",
]);
export const COUNTERPARTY_PAYMENT_METHOD_LABELS = {
    CASH: "Nakit",
    BANK_TRANSFER: "Havale / EFT",
    CREDIT_CARD: "Kredi kartı",
    CHEQUE: "Çek",
    NOTE: "Senet",
    DBS: "DBS",
    OTHER: "Diğer",
};
/** Limit aşımında davranış (cari bazında). */
export const limitPolicySchema = z.enum(["WARN", "APPROVAL", "BLOCK"]);
export const LIMIT_POLICY_LABELS = {
    WARN: "Uyar",
    APPROVAL: "Onaya gönder",
    BLOCK: "Engelle",
};
export const kvkkConsentStatusSchema = z.enum(["NOT_ASKED", "GRANTED", "DENIED", "WITHDRAWN"]);
export const KVKK_CONSENT_LABELS = {
    NOT_ASKED: "Sorulmadı",
    GRANTED: "Verildi",
    DENIED: "Verilmedi",
    WITHDRAWN: "Geri alındı",
};
/**
 * Carinin izlendiği kontrol hesabının ANLAMSAL anahtarı (hesap kodu değil). Varsayılanlar: 120, 320, 196, 135, 340, 159.
 * `side`: hesabın doğal yönü — RECEIVABLE yönlü hesapta borç bakiyesi "cari bize borçlu" demektir.
 */
export const CONTROL_KEYS = {
    CUSTOMER_RECEIVABLE: { label: "Alıcılar (120)", side: "RECEIVABLE" },
    SUPPLIER_PAYABLE: { label: "Satıcılar (320)", side: "PAYABLE" },
    PERSONNEL_ADVANCE: { label: "Personel avansları (196)", side: "RECEIVABLE" },
    PERSONNEL_RECEIVABLE: { label: "Personelden alacaklar (135)", side: "RECEIVABLE" },
    ADVANCES_RECEIVED: { label: "Alınan sipariş avansları (340)", side: "PAYABLE" },
    ADVANCES_GIVEN: { label: "Verilen sipariş avansları (159)", side: "RECEIVABLE" },
    /** Karşılıksız çek / protestolu senet alacağı (Prompt 09); dekontla elle de kullanılabilir. */
    DOUBTFUL_RECEIVABLE: { label: "Şüpheli ticari alacaklar (128)", side: "RECEIVABLE" },
};
export const CONTROL_KEY_NAMES = Object.keys(CONTROL_KEYS);
export const controlKeySchema = z.enum(CONTROL_KEY_NAMES);
export const openItemDirectionSchema = z.enum(["RECEIVABLE", "PAYABLE"]);
export const OPEN_ITEM_DIRECTION_LABELS = {
    RECEIVABLE: "Alacak (cari bize borçlu)",
    PAYABLE: "Borç (biz cariye borçluyuz)",
};
export const openItemStatusSchema = z.enum(["OPEN", "CLOSED", "VOID"]);
export const OPEN_ITEM_STATUS_LABELS = {
    OPEN: "Açık",
    CLOSED: "Kapalı",
    VOID: "İptal",
};
export const openItemKindSchema = z.enum([
    "INVOICE",
    "COLLECTION",
    "PAYMENT",
    "DEBIT_NOTE",
    "CREDIT_NOTE",
    "TRANSFER",
    "OPENING",
    "CHEQUE_RETURN",
    "OTHER",
]);
export const OPEN_ITEM_KIND_LABELS = {
    INVOICE: "Fatura",
    COLLECTION: "Tahsilat",
    PAYMENT: "Ödeme",
    DEBIT_NOTE: "Borç dekontu",
    CREDIT_NOTE: "Alacak dekontu",
    TRANSFER: "Virman",
    OPENING: "Açılış",
    CHEQUE_RETURN: "Çek iadesi",
    OTHER: "Diğer",
};
export const counterpartyTxnTypeSchema = z.enum([
    "COLLECTION",
    "PAYMENT",
    "DEBIT_NOTE",
    "CREDIT_NOTE",
    "TRANSFER",
    "OPENING",
]);
export const COUNTERPARTY_TXN_TYPE_LABELS = {
    COLLECTION: "Tahsilat",
    PAYMENT: "Ödeme",
    DEBIT_NOTE: "Borç dekontu",
    CREDIT_NOTE: "Alacak dekontu",
    TRANSFER: "Virman",
    OPENING: "Açılış bakiyesi",
};
/** Tahsilat/ödeme kanalı. Gerçek kasa/banka/POS/çek servisleri Prompt 08–09'da bağlanır (PaymentChannelPort). */
export const paymentChannelSchema = z.enum(["CASH", "BANK", "POS", "CHEQUE", "NOTE"]);
export const PAYMENT_CHANNEL_LABELS = {
    CASH: "Nakit",
    BANK: "Banka (havale / EFT)",
    POS: "POS (kredi kartı)",
    CHEQUE: "Çek",
    NOTE: "Senet",
};
// ── Cari kart ────────────────────────────────────────────────────────────────
export const counterpartyAddressSchema = z.object({
    type: z.enum(["BILLING", "SHIPPING"]),
    line: z.string().trim().min(1, "Adres zorunludur").max(500),
    district: optionalText(60),
    city: optionalText(60),
    postalCode: optionalText(10),
    country: z.string().trim().max(60).default("Türkiye"),
});
export const counterpartyContactSchema = z.object({
    name: z.string().trim().min(1, "Yetkili adı zorunludur").max(200),
    title: optionalText(100),
    phone: optionalText(30),
    email: z.email("Geçerli bir e-posta girin").max(200).nullable().default(null),
});
export const counterpartyBankAccountSchema = z.object({
    bankName: optionalText(100),
    iban: z
        .string()
        .trim()
        .transform((value) => value.replace(/\s+/g, "").toUpperCase())
        .pipe(z.string().regex(/^TR\d{24}$/, "IBAN TR ile başlayan 26 karakter olmalıdır")),
    holder: optionalText(200),
});
export const counterpartyCompanyLinkSchema = z.object({
    companyId: uuidSchema,
    companyName: z.string(),
    receivableAccountId: uuidSchema.nullable(),
    receivableAccountCode: z.string().nullable(),
    payableAccountId: uuidSchema.nullable(),
    payableAccountCode: z.string().nullable(),
});
/** KVKK: gerçek kişide ad, TCKN, telefon, e-posta, adres, yetkili ve IBAN kişisel veridir (x-personal-data). */
export const counterpartySchema = z.object({
    id: uuidSchema,
    code: z.string(),
    kind: counterpartyKindSchema,
    name: z.string().describe("Ünvan ya da ad soyad (kişisel veri)"),
    roles: z.array(counterpartyRoleSchema),
    taxId: z.string().nullable().describe("VKN / TCKN (kişisel veri)"),
    taxIdKind: z.enum(["VKN", "TCKN"]).nullable(),
    foreignTaxId: z.string().nullable(),
    countryCode: z.string(),
    taxOffice: z.string().nullable(),
    isEInvoiceRegistered: z.boolean(),
    eInvoiceAlias: z.string().nullable(),
    phone: z.string().nullable().describe("Kişisel veri"),
    email: z.string().nullable().describe("Kişisel veri"),
    addresses: z.array(counterpartyAddressSchema),
    contacts: z.array(counterpartyContactSchema),
    bankAccounts: z.array(counterpartyBankAccountSchema),
    tags: z.array(z.string()),
    paymentTermDays: z.number().int(),
    paymentMethod: counterpartyPaymentMethodSchema.nullable(),
    discountRate: z.string(),
    creditLimit: moneySchema.nullable().describe("null = limitsiz"),
    chequeRiskLimit: moneySchema.nullable(),
    limitPolicy: limitPolicySchema,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
    currency: z.string(),
    distributorId: uuidSchema.nullable(),
    kvkkNoticeVersion: z.string().nullable(),
    kvkkNoticeAt: timestampSchema.nullable(),
    kvkkConsentStatus: kvkkConsentStatusSchema,
    kvkkConsentVersion: z.string().nullable(),
    kvkkConsentAt: timestampSchema.nullable(),
    isActive: z.boolean(),
    companies: z.array(counterpartyCompanyLinkSchema),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
const counterpartyWritableShape = {
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, "Kod büyük harf, rakam, nokta, tire ve alt çizgi içerebilir")
        .nullable()
        .default(null)
        .describe("Boş bırakılırsa otomatik üretilir (C000001)"),
    kind: counterpartyKindSchema,
    name: z.string().trim().min(1, "Ünvan / ad soyad zorunludur").max(300),
    roles: z.array(counterpartyRoleSchema).min(1, "En az bir rol seçin").max(6),
    taxId: z
        .string()
        .trim()
        .regex(/^\d{10,11}$/, "VKN 10, TCKN 11 haneli olmalıdır")
        .nullable()
        .default(null),
    foreignTaxId: optionalText(50),
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/, "Ülke kodu 2 harfli olmalıdır")
        .default("TR"),
    taxOffice: optionalText(100),
    isEInvoiceRegistered: z.boolean().default(false),
    eInvoiceAlias: optionalText(200),
    phone: optionalText(30),
    email: z.email("Geçerli bir e-posta girin").max(200).nullable().default(null),
    addresses: z.array(counterpartyAddressSchema).max(10).default([]),
    contacts: z.array(counterpartyContactSchema).max(20).default([]),
    bankAccounts: z.array(counterpartyBankAccountSchema).max(10).default([]),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    paymentTermDays: z.number().int().min(0).max(3650).default(0),
    paymentMethod: counterpartyPaymentMethodSchema.nullable().default(null),
    discountRate: rateSchema.default("0"),
    creditLimit: nonNegativeMoneySchema.nullable().default(null),
    chequeRiskLimit: nonNegativeMoneySchema.nullable().default(null),
    limitPolicy: limitPolicySchema.default("WARN"),
    isBlocked: z.boolean().default(false),
    blockReason: optionalText(500),
    distributorId: uuidSchema.nullable().default(null),
    kvkkNoticeVersion: optionalText(40),
    kvkkConsentStatus: kvkkConsentStatusSchema.default("NOT_ASKED"),
    kvkkConsentVersion: optionalText(40),
};
export const createCounterpartySchema = z.object({
    ...counterpartyWritableShape,
    companyIds: z.array(uuidSchema).min(1, "Carinin çalışacağı en az bir şirket seçin").max(50),
    allowDuplicateTaxId: z
        .boolean()
        .default(false)
        .describe("Aynı VKN/TCKN ile kart varsa 409 DUPLICATE_TAX_ID döner; true ile yine de açılır"),
});
export const updateCounterpartySchema = patchOf(z.object({ ...counterpartyWritableShape, isActive: z.boolean(), allowDuplicateTaxId: z.boolean() }))
    .omit({ code: true })
    .extend({ version: z.number().int().positive() });
export const counterpartyCompanyParamsSchema = z.object({ id: uuidSchema, companyId: uuidSchema });
export const setCounterpartyCompanySchema = z.object({
    receivableAccountId: uuidSchema
        .nullable()
        .default(null)
        .describe("Carinin alacak alt hesabı (ör. 120.01.0042)"),
    payableAccountId: uuidSchema.nullable().default(null).describe("Carinin borç alt hesabı (ör. 320.01.0042)"),
});
export const counterpartySummarySchema = z.object({
    id: uuidSchema,
    code: z.string(),
    kind: counterpartyKindSchema,
    name: z.string(),
    roles: z.array(counterpartyRoleSchema),
    taxId: z.string().nullable(),
    tags: z.array(z.string()),
    isBlocked: z.boolean(),
    isActive: z.boolean(),
    balance: moneySchema.describe("İşaretli: + cari bize borçlu, − biz cariye borçluyuz"),
    overdueReceivable: moneySchema,
    overduePayable: moneySchema,
    risk: moneySchema,
    creditLimit: moneySchema.nullable(),
});
export const counterpartyPageSchema = pageOf(counterpartySummarySchema);
export const listCounterpartiesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema
        .optional()
        .describe("Bakiye kolonları bu şirket için; verilmezse görünür şirketlerin toplamı"),
    search: z.string().trim().max(100).optional().describe("Kod, ünvan ya da VKN/TCKN"),
    role: counterpartyRoleSchema.optional(),
    tag: z.string().trim().max(40).optional(),
    isActive: queryBoolean.optional(),
    isBlocked: queryBoolean.optional(),
    onlyWithBalance: queryBoolean.optional(),
});
export const counterpartyNoteSchema = z.object({
    id: uuidSchema,
    body: z.string(),
    createdBy: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    createdAt: timestampSchema,
});
export const counterpartyNoteListSchema = z.object({ items: z.array(counterpartyNoteSchema) });
export const createCounterpartyNoteSchema = z.object({ body: z.string().trim().min(1).max(4000) });
// ── Açık kalemler ve eşleştirme ──────────────────────────────────────────────
export const openItemSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    direction: openItemDirectionSchema,
    controlKey: controlKeySchema,
    itemKind: openItemKindSchema,
    sourceModule: z.string(),
    sourceDocType: z.string(),
    sourceDocId: uuidSchema,
    docNo: z.string().nullable(),
    description: z.string().nullable(),
    documentDate: localDateSchema,
    dueDate: localDateSchema,
    amount: moneySchema,
    remainingAmount: moneySchema,
    netAmount: moneySchema.nullable(),
    overdueDays: z.number().int().describe("Bugüne göre; vadesi gelmemişse 0"),
    status: openItemStatusSchema,
});
export const openItemPageSchema = pageOf(openItemSchema);
export const listOpenItemsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    counterpartyId: uuidSchema.optional(),
    direction: openItemDirectionSchema.optional(),
    status: openItemStatusSchema.optional(),
    dueTo: localDateSchema.optional(),
});
export const allocationSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    receivableItemId: uuidSchema,
    receivableDocNo: z.string().nullable(),
    payableItemId: uuidSchema,
    payableDocNo: z.string().nullable(),
    amount: moneySchema,
    method: z.enum(["AUTO_FIFO", "MANUAL"]),
    isCrossAccount: z.boolean(),
    status: z.enum(["ACTIVE", "REVERSED"]),
    allocatedOn: localDateSchema,
    createdAt: timestampSchema,
    reversedAt: timestampSchema.nullable(),
    reverseReason: z.string().nullable(),
});
export const allocationListSchema = z.object({ items: z.array(allocationSchema) });
export const listAllocationsQuerySchema = z
    .object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema.optional(),
    openItemId: uuidSchema.optional(),
    includeReversed: queryBoolean.optional(),
})
    .refine((query) => query.counterpartyId ?? query.openItemId, "counterpartyId ya da openItemId verilmelidir");
export const allocationPairSchema = z.object({
    receivableItemId: uuidSchema,
    payableItemId: uuidSchema,
    amount: positiveMoneySchema,
});
/** Elle eşleştirme: verilen çiftler; `pairs` yoksa carinin TÜM açık kalemleri FIFO (vadesi en eski önce) eşleştirilir. */
export const createAllocationsSchema = z.object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    pairs: z.array(allocationPairSchema).min(1).max(500).optional(),
    allocatedOn: localDateSchema.optional().describe("Mahsup fişi doğarsa kayıt tarihi; varsayılan bugün"),
});
export const reverseAllocationSchema = z.object({
    reason: z.string().trim().min(3, "Gerekçe zorunludur").max(500),
});
// ── Hareketler ───────────────────────────────────────────────────────────────
const txnBaseShape = {
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    documentDate: localDateSchema,
    amount: positiveMoneySchema,
    description: z.string().trim().min(1, "Açıklama zorunludur").max(500),
    docNo: optionalText(60),
    stationId: uuidSchema.nullable().default(null),
    controlKey: controlKeySchema
        .optional()
        .describe("Verilmezse carinin rolüne ve hareket türüne göre seçilir"),
};
/** Tahsilat/ödemenin açık kalemlere uygulanması. */
export const settlementSchema = z
    .discriminatedUnion("mode", [
    z.object({ mode: z.literal("NONE") }),
    z.object({ mode: z.literal("AUTO_FIFO") }),
    z.object({
        mode: z.literal("MANUAL"),
        items: z
            .array(z.object({ openItemId: uuidSchema, amount: positiveMoneySchema }))
            .min(1)
            .max(500),
    }),
])
    .default({ mode: "AUTO_FIFO" });
const paymentShape = {
    ...txnBaseShape,
    channel: paymentChannelSchema,
    channelRefId: uuidSchema
        .nullable()
        .default(null)
        .describe("Kasa / banka hesabı / POS / çek kimliği (Prompt 08–09'da zorunlu olacak)"),
    settlement: settlementSchema,
};
const noteShape = {
    ...txnBaseShape,
    dueDate: localDateSchema.optional().describe("Verilmezse belge tarihi + carinin vade günü"),
    netAmount: positiveMoneySchema
        .nullable()
        .default(null)
        .describe("KDV hariç tutar; doluysa belge Ba/Bs raporuna girer"),
    offsetKey: postingKeyRefSchema.describe("Karşı hesap anahtarı (ör. OTHER_INCOME, EXPENSE:RENT)"),
};
export const createCounterpartyTransactionSchema = z.discriminatedUnion("txnType", [
    z.object({ txnType: z.literal("COLLECTION"), ...paymentShape }),
    z.object({ txnType: z.literal("PAYMENT"), ...paymentShape }),
    z.object({ txnType: z.literal("DEBIT_NOTE"), ...noteShape }),
    z.object({ txnType: z.literal("CREDIT_NOTE"), ...noteShape }),
    z.object({
        txnType: z.literal("TRANSFER"),
        ...txnBaseShape,
        transferDirection: openItemDirectionSchema.describe("Kaynak caride oluşacak kalemin yönü. PAYABLE: kaynağın borcu azalır/alacağı doğar → hedef cari borçlanır"),
        targetCounterpartyId: uuidSchema,
        targetControlKey: controlKeySchema.optional(),
    }),
    z.object({
        txnType: z.literal("OPENING"),
        ...txnBaseShape,
        direction: openItemDirectionSchema,
        dueDate: localDateSchema,
    }),
]);
export const counterpartyTransactionSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    txnNo: z.string().describe("Görüntüleme numarası: CH2026000042"),
    txnType: counterpartyTxnTypeSchema,
    documentDate: localDateSchema,
    dueDate: localDateSchema,
    amount: moneySchema,
    netAmount: moneySchema.nullable(),
    controlKey: controlKeySchema,
    direction: openItemDirectionSchema,
    channel: paymentChannelSchema.nullable(),
    channelRefId: uuidSchema.nullable(),
    offsetKey: z.string().nullable(),
    targetCounterpartyId: uuidSchema.nullable(),
    targetCounterpartyName: z.string().nullable(),
    targetControlKey: controlKeySchema.nullable(),
    stationId: uuidSchema.nullable(),
    docNo: z.string().nullable(),
    description: z.string(),
    status: z.enum(["POSTED", "CANCELLED"]),
    ledgerStatus: z.enum(["POSTED", "QUEUED", "NONE"]),
    journalEntryId: uuidSchema.nullable(),
    cancelReason: z.string().nullable(),
    cancelledAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const counterpartyTransactionPageSchema = pageOf(counterpartyTransactionSchema);
export const counterpartyTransactionResultSchema = z.object({
    transaction: counterpartyTransactionSchema,
    allocations: z.array(allocationSchema),
    unallocatedAmount: moneySchema.describe("Eşleşmeyen kısım avans gibi açık kalır"),
});
export const listCounterpartyTransactionsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    counterpartyId: uuidSchema.optional(),
    txnType: counterpartyTxnTypeSchema.optional(),
    dateFrom: localDateSchema.optional(),
    dateTo: localDateSchema.optional(),
    includeCancelled: queryBoolean.optional(),
});
export const cancelCounterpartyTransactionSchema = z.object({
    reason: z.string().trim().min(3, "Gerekçe zorunludur").max(500),
    version: z.number().int().positive(),
});
// Açılış bakiyesi aktarımı (Excel/CSV; açık kalem bazında, vadeli)
export const counterpartyOpeningImportRequestSchema = z.object({
    companyId: uuidSchema,
    fileId: uuidSchema.describe("`POST /files` ile yüklenmiş Excel/CSV dosyası (purpose: IMPORT)"),
});
export const counterpartyOpeningImportPreviewSchema = z.object({
    valid: z.boolean(),
    totalReceivable: moneySchema,
    totalPayable: moneySchema,
    rows: z.array(z.object({
        rowNo: z.number().int(),
        counterpartyCode: z.string(),
        counterpartyName: z.string().nullable(),
        direction: openItemDirectionSchema.nullable(),
        amount: moneySchema,
        documentDate: localDateSchema.nullable(),
        dueDate: localDateSchema.nullable(),
        docNo: z.string().nullable(),
        errors: z.array(z.string()),
    })),
    errors: z.array(z.string()),
});
export const counterpartyOpeningImportResultSchema = z.object({
    createdCount: z.number().int(),
    totalReceivable: moneySchema,
    totalPayable: moneySchema,
});
// ── Risk ve limit ────────────────────────────────────────────────────────────
export const counterpartyRiskSchema = z.object({
    counterpartyId: uuidSchema,
    companyId: uuidSchema,
    openBalance: moneySchema.describe("Açık (net) alacak bakiyesi; negatifse 0 sayılır"),
    chequesInPortfolio: moneySchema,
    endorsedChequesNotDue: moneySchema.describe("Ciro edilmiş ama vadesi gelmemiş müşteri çekleri"),
    undeliveredOrders: moneySchema,
    totalRisk: moneySchema,
    creditLimit: moneySchema.nullable(),
    available: moneySchema.nullable().describe("Limit − risk; limitsizse null"),
    chequeRisk: moneySchema,
    chequeRiskLimit: moneySchema.nullable(),
    overdueReceivable: moneySchema,
    oldestOverdueDays: z.number().int(),
    limitPolicy: limitPolicySchema,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
});
export const counterpartyRiskQuerySchema = z.object({ companyId: uuidSchema });
export const creditCheckRequestSchema = z.object({
    companyId: uuidSchema,
    amount: positiveMoneySchema.describe("Yapılmak istenen veresiye / vadeli satışın tutarı"),
});
export const creditVerdictSchema = z.enum(["ALLOWED", "WARN", "APPROVAL_REQUIRED", "BLOCKED"]);
export const creditCheckResultSchema = z.object({
    verdict: creditVerdictSchema,
    reasons: z.array(z.enum(["COUNTERPARTY_BLOCKED", "CREDIT_LIMIT_EXCEEDED", "COUNTERPARTY_INACTIVE"])),
    risk: counterpartyRiskSchema,
    riskAfter: moneySchema,
});
/** İstasyon ajanının internetsiz çalışma için önbelleğe aldığı özet. Kişisel veri İÇERMEZ (kod + kimlik). */
export const limitSummaryItemSchema = z.object({
    counterpartyId: uuidSchema,
    code: z.string(),
    creditLimit: moneySchema.nullable(),
    totalRisk: moneySchema,
    available: moneySchema.nullable(),
    limitPolicy: limitPolicySchema,
    isBlocked: z.boolean(),
    isActive: z.boolean(),
});
export const limitSummarySchema = z.object({
    companyId: uuidSchema,
    generatedAt: timestampSchema,
    items: z.array(limitSummaryItemSchema),
});
export const limitSummaryQuerySchema = z.object({
    companyId: uuidSchema,
    roles: z
        .string()
        .optional()
        .describe("Virgülle ayrılmış roller; varsayılan CUSTOMER,FLEET_CUSTOMER")
        .transform((value) => (value ? value.split(",") : ["CUSTOMER", "FLEET_CUSTOMER"]))
        .pipe(z.array(counterpartyRoleSchema)),
});
export const riskBoardEntrySchema = z.object({
    counterpartyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    totalRisk: moneySchema,
    creditLimit: moneySchema.nullable(),
    usagePercent: z.number().int().nullable(),
    overdueReceivable: moneySchema,
    oldestOverdueDays: z.number().int(),
    isBlocked: z.boolean(),
});
export const riskBoardSchema = z.object({
    companyId: uuidSchema,
    nearLimitPercent: z.number().int(),
    overLimit: z.array(riskBoardEntrySchema),
    nearLimit: z.array(riskBoardEntrySchema),
    overdue: z.array(riskBoardEntrySchema),
});
export const riskBoardQuerySchema = z.object({
    companyId: uuidSchema,
    nearLimitPercent: z.coerce.number().int().min(1).max(99).default(80),
});
// ── Raporlar ─────────────────────────────────────────────────────────────────
const fileResultShape = { fileName: z.string(), mimeType: z.string(), contentBase64: z.string() };
export const reportFileSchema = z.object(fileResultShape);
export const counterpartyStatementQuerySchema = z
    .object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    dateFrom: localDateSchema,
    dateTo: localDateSchema,
})
    .refine((query) => query.dateFrom <= query.dateTo, "Başlangıç tarihi bitişten sonra olamaz");
export const counterpartyStatementSchema = z.object({
    companyId: uuidSchema,
    companyName: z.string(),
    counterpartyId: uuidSchema,
    counterpartyCode: z.string(),
    counterpartyName: z.string(),
    dateFrom: localDateSchema,
    dateTo: localDateSchema,
    openingBalance: moneySchema.describe("Devir; işaretli (+ cari bize borçlu)"),
    lines: z.array(z.object({
        itemId: uuidSchema,
        documentDate: localDateSchema,
        dueDate: localDateSchema,
        itemKind: openItemKindSchema,
        docNo: z.string().nullable(),
        description: z.string().nullable(),
        debit: moneySchema.describe("Cariyi borçlandıran (RECEIVABLE)"),
        credit: moneySchema.describe("Cariyi alacaklandıran (PAYABLE)"),
        balance: moneySchema.describe("Yürüyen bakiye"),
    })),
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    closingBalance: moneySchema,
});
export const sendStatementSchema = z.object({
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    dateFrom: localDateSchema,
    dateTo: localDateSchema,
    to: z.email("Geçerli bir e-posta girin").optional().describe("Verilmezse carinin e-postası"),
});
export const sentSchema = z.object({ sent: z.literal(true), to: z.string().describe("Maskeli alıcı") });
export const DEFAULT_AGING_BOUNDARIES = [30, 60, 90];
export const agingQuerySchema = z.object({
    companyId: uuidSchema,
    direction: openItemDirectionSchema.default("RECEIVABLE"),
    asOf: localDateSchema.optional(),
    boundaries: z
        .string()
        .optional()
        .describe("Virgülle ayrılmış artan gün sınırları; varsayılan 30,60,90")
        .transform((value) => (value ? value.split(",").map(Number) : [...DEFAULT_AGING_BOUNDARIES]))
        .pipe(z
        .array(z.number().int().min(1).max(3650))
        .min(1)
        .max(6)
        .refine((list) => list.every((value, index) => index === 0 || value > list[index - 1]), "Sınırlar artan sırada olmalıdır")),
});
export const agingReportSchema = z.object({
    companyId: uuidSchema,
    direction: openItemDirectionSchema,
    asOf: localDateSchema,
    bucketLabels: z.array(z.string()).describe('Örn. ["Vadesi gelmemiş","0–30","31–60","61–90","90+"]'),
    rows: z.array(z.object({
        counterpartyId: uuidSchema,
        code: z.string(),
        name: z.string(),
        buckets: z.array(moneySchema),
        total: moneySchema,
    })),
    totals: z.array(moneySchema),
    grandTotal: moneySchema,
});
export const dueCalendarQuerySchema = z
    .object({ companyId: uuidSchema, dateFrom: localDateSchema, dateTo: localDateSchema })
    .refine((query) => query.dateFrom <= query.dateTo, "Başlangıç tarihi bitişten sonra olamaz");
export const dueCalendarSchema = z.object({
    companyId: uuidSchema,
    days: z.array(z.object({
        dueDate: localDateSchema,
        receivable: moneySchema,
        payable: moneySchema,
        items: z.array(openItemSchema),
    })),
    totalReceivable: moneySchema,
    totalPayable: moneySchema,
});
export const baBsFormSchema = z.enum(["BA", "BS"]);
export const baBsQuerySchema = z.object({
    companyId: uuidSchema,
    form: baBsFormSchema,
    year: z.coerce.number().int().min(2000).max(2999),
    month: z.coerce.number().int().min(1).max(12),
});
export const baBsReportSchema = z.object({
    companyId: uuidSchema,
    form: baBsFormSchema,
    year: z.number().int(),
    month: z.number().int(),
    threshold: moneySchema,
    thresholdNeedsVerification: z.boolean(),
    rows: z.array(z.object({
        counterpartyId: uuidSchema,
        code: z.string(),
        name: z.string(),
        taxId: z.string().nullable(),
        countryCode: z.string(),
        documentCount: z.number().int(),
        netTotal: moneySchema.describe("KDV hariç toplam"),
    })),
    belowThresholdCount: z.number().int().describe("Eşiğin altında kaldığı için rapora girmeyen cari sayısı"),
    netTotal: moneySchema,
});
// ── Mutabakat mektupları ─────────────────────────────────────────────────────
export const reconciliationLetterTypeSchema = z.enum(["BALANCE", "BA", "BS"]);
export const RECONCILIATION_LETTER_TYPE_LABELS = {
    BALANCE: "Cari bakiye mutabakatı",
    BA: "Ba mutabakatı (alışlar)",
    BS: "Bs mutabakatı (satışlar)",
};
export const reconciliationLetterStatusSchema = z.enum(["DRAFT", "SENT", "AGREED", "DISAGREED", "CANCELLED"]);
export const RECONCILIATION_LETTER_STATUS_LABELS = {
    DRAFT: "Taslak",
    SENT: "Gönderildi",
    AGREED: "Mutabık",
    DISAGREED: "Mutabık değil",
    CANCELLED: "İptal",
};
export const reconciliationLetterSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyCode: z.string(),
    counterpartyName: z.string(),
    letterType: reconciliationLetterTypeSchema,
    asOfDate: localDateSchema.nullable(),
    periodYear: z.number().int().nullable(),
    periodMonth: z.number().int().nullable(),
    amount: moneySchema,
    documentCount: z.number().int().nullable(),
    status: reconciliationLetterStatusSchema,
    sentAt: timestampSchema.nullable(),
    respondedAt: timestampSchema.nullable(),
    responseNote: z.string().nullable(),
    responseFileId: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const reconciliationLetterPageSchema = pageOf(reconciliationLetterSchema);
export const listReconciliationLettersQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    counterpartyId: uuidSchema.optional(),
    status: reconciliationLetterStatusSchema.optional(),
    letterType: reconciliationLetterTypeSchema.optional(),
});
export const createReconciliationLettersSchema = z.discriminatedUnion("letterType", [
    z.object({
        letterType: z.literal("BALANCE"),
        companyId: uuidSchema,
        counterpartyIds: z.array(uuidSchema).min(1).max(500),
        asOfDate: localDateSchema,
    }),
    z.object({
        letterType: z.enum(["BA", "BS"]),
        companyId: uuidSchema,
        counterpartyIds: z.array(uuidSchema).max(500).optional().describe("Verilmezse eşiği aşan tüm cariler"),
        year: z.number().int().min(2000).max(2999),
        month: z.number().int().min(1).max(12),
    }),
]);
export const reconciliationLetterListSchema = z.object({ items: z.array(reconciliationLetterSchema) });
export const sendReconciliationLetterSchema = z.object({
    to: z.email("Geçerli bir e-posta girin").optional(),
    version: z.number().int().positive(),
});
export const respondReconciliationLetterSchema = z.object({
    outcome: z.enum(["AGREED", "DISAGREED"]),
    note: optionalText(2000),
    fileId: uuidSchema.nullable().default(null).describe("Carinin imzalı yanıtı (ek)"),
    version: z.number().int().positive(),
});
export const cancelReconciliationLetterSchema = z.object({ version: z.number().int().positive() });
// ── Tahsilat hatırlatma ──────────────────────────────────────────────────────
export const sendCollectionReminderSchema = z.object({
    companyId: uuidSchema,
    channel: z.enum(["EMAIL", "SMS"]),
});
export const collectionReminderResultSchema = z.object({
    sent: z.literal(true),
    channel: z.enum(["EMAIL", "SMS"]),
    to: z.string().describe("Maskeli alıcı"),
    overdueAmount: moneySchema,
    itemCount: z.number().int(),
});
// ── Alt defter ↔ ana defter mutabakatı ───────────────────────────────────────
export const ledgerDiffSchema = z.object({
    counterpartyId: uuidSchema.nullable().describe("null = hesap düzeyi (açılış ayrıntısı ↔ açılış fişi)"),
    counterpartyCode: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    accountId: uuidSchema,
    accountCode: z.string(),
    subledgerBalance: moneySchema,
    ledgerBalance: moneySchema,
    difference: moneySchema,
});
export const ledgerReconciliationSchema = z.object({
    companyId: uuidSchema,
    runId: uuidSchema.nullable(),
    ranAt: timestampSchema.nullable(),
    asOf: localDateSchema.nullable(),
    trigger: z.enum(["NIGHTLY", "MANUAL"]).nullable(),
    checkedCount: z.number().int(),
    diffs: z.array(ledgerDiffSchema),
});
export const ledgerReconciliationQuerySchema = z.object({ companyId: uuidSchema });
export const runLedgerReconciliationSchema = z.object({ companyId: uuidSchema });
//# sourceMappingURL=counterparties.js.map